import React, { useState, useEffect, useCallback } from 'react';
import { licensesApi, clientsApi, contratsApi, GenerateLicenseInput, ClientInput } from '../services/api';
import { License, Client, LICENSE_TYPES, PAYMENT_MODES, WILAYAS } from '../types';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import AuditHistoryModal from '../components/AuditHistoryModal';
import SearchableSelect from '../components/SearchableSelect';
import './Pages.css';

const ITEMS_PER_PAGE = 10;

const DEFAULT_NEW_CONTRAT_PAYMENT_MODE = PAYMENT_MODES.includes('Nouvelle inst.')
  ? 'Nouvelle inst.'
  : PAYMENT_MODES[0] || 'Nouvelle inst.';

type PendingLicenseGeneration = {
  clientId: number;
  clientName: string;
  formData: GenerateLicenseInput;
  isNewClient: boolean;
  newClient: ClientInput;
  hasContrat: boolean;
  contractCreated?: boolean;
};

type MissingContratPromptState = {
  pending: PendingLicenseGeneration;
  modeReglt: string;
};

const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EditIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 16, height: 16 }}>
    <path
      d="M4 17.25V20h2.75L17.8 8.94l-2.75-2.75L4 17.25Zm14.71-9.04a1 1 0 0 0 0-1.42l-1.5-1.5a1 1 0 0 0-1.42 0l-1.17 1.17 2.75 2.75 1.34-1.34Z"
      fill="currentColor"
    />
  </svg>
);

const Licenses: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [auditLicense, setAuditLicense] = useState<License | null>(null);
  const { showToast, showConfirm } = useToast();
  const { hasPermission } = useAuth();
  const canManageLicenses = hasPermission('licenses', 'delete');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<GenerateLicenseInput>({
    client_id: 0,
    hardware_id: '',
    license_type: 'WINALCO PAIE',
    notes: '',
  });
  const [generatedKey, setGeneratedKey] = useState('');
  const [isExistingKey, setIsExistingKey] = useState(false);
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClient, setNewClient] = useState<ClientInput>({
    client_name: '',
    telephone: '',
    email: '',
    wilaya: '',
    ville: '',
  });
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [editingClientId, setEditingClientId] = useState(0);
  const [savingLicenseClient, setSavingLicenseClient] = useState(false);
  const [submittingGeneration, setSubmittingGeneration] = useState(false);
  const [missingContratPrompt, setMissingContratPrompt] = useState<MissingContratPromptState | null>(null);

  // Verify modal
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyData, setVerifyData] = useState({ activation_key: '', hardware_id: '' });
  const [verifyResult, setVerifyResult] = useState<any>(null);

  // Password encoder
  const [showEncoderModal, setShowEncoderModal] = useState(false);
  const [textToEncode, setTextToEncode] = useState('');
  const [encodedText, setEncodedText] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [licensesRes, clientsRes] = await Promise.all([
        licensesApi.getAll(),
        clientsApi.getAll(),
      ]);
      const licensesData = Array.isArray(licensesRes.data) ? licensesRes.data : licensesRes.data?.data || licensesRes.data?.licenses || [];
      const clientsData = Array.isArray(clientsRes.data) ? clientsRes.data : clientsRes.data?.data || clientsRes.data?.clients || [];
      setLicenses(licensesData);
      setClients(clientsData);
    } catch (err: any) {
      console.error('Erreur chargement licences:', err?.response?.status, err?.response?.data, err?.message);
      const msg = err?.response?.status
        ? `Erreur ${err.response.status}: ${err.response.data?.error || err.response.data?.message || err.message}`
        : `Erreur réseau: ${err?.message || 'Serveur inaccessible'}`;
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterType, filterStatus]);

  // Filtrage
  const filteredLicenses = licenses.filter((license) => {
    if (search && !license.client_name.toLowerCase().includes(search.toLowerCase()) && !license.hardware_id.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType && !license.license_type.toLowerCase().includes(filterType.toLowerCase())) return false;
    if (filterStatus === 'active' && !license.is_active) return false;
    if (filterStatus === 'inactive' && license.is_active) return false;
    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredLicenses.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLicenses = filteredLicenses.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleGenerate = () => {
    setFormData({
      client_id: 0,
      hardware_id: '',
      license_type: 'WINALCO PAIE',
      notes: '',
    });
    setGeneratedKey('');
    setIsExistingKey(false);
    setIsNewClient(false);
    setNewClient({ client_name: '', telephone: '', email: '', wilaya: '', ville: '' });
    setMissingContratPrompt(null);
    setShowModal(true);
  };

  const closeGenerateModal = () => {
    if (submittingGeneration) {
      return;
    }

    setMissingContratPrompt(null);
    setShowModal(false);
  };

  const getClientHasContrat = async (clientId: number): Promise<boolean> => {
    const selectedClient = clients.find((client) => client.id === clientId);
    if (typeof selectedClient?.has_contrats === 'boolean') {
      return selectedClient.has_contrats;
    }

    const contratsRes = await contratsApi.getAll();
    const contratsData = Array.isArray(contratsRes.data)
      ? contratsRes.data
      : contratsRes.data?.data || contratsRes.data?.contrats || [];

    return contratsData.some((contrat: any) => Number(contrat.id_client) === clientId);
  };

  const buildPendingGeneration = async (): Promise<PendingLicenseGeneration | null> => {
    const trimmedHardwareId = formData.hardware_id.trim();

    if (trimmedHardwareId.length !== 32) {
      showToast('Le Hardware ID doit faire exactement 32 caracteres', 'error');
      return null;
    }

    const normalizedFormData: GenerateLicenseInput = {
      ...formData,
      hardware_id: trimmedHardwareId,
      notes: formData.notes || '',
    };

    if (isNewClient) {
      const normalizedClient: ClientInput = {
        client_name: newClient.client_name.trim(),
        telephone: newClient.telephone.trim(),
        email: newClient.email.trim(),
        wilaya: newClient.wilaya,
        ville: newClient.ville.trim(),
      };

      if (!normalizedClient.client_name) {
        showToast('Le nom du client est requis', 'error');
        return null;
      }

      return {
        clientId: 0,
        clientName: normalizedClient.client_name,
        formData: normalizedFormData,
        isNewClient: true,
        newClient: normalizedClient,
        hasContrat: false,
      };
    }

    if (!formData.client_id || formData.client_id === 0) {
      showToast('Veuillez selectionner ou creer un client', 'error');
      return null;
    }

    const selectedClient = clients.find((client) => client.id === formData.client_id);
    const hasContrat = await getClientHasContrat(formData.client_id);

    return {
      clientId: formData.client_id,
      clientName: selectedClient?.client_name || 'ce client',
      formData: normalizedFormData,
      isNewClient: false,
      newClient: { ...newClient },
      hasContrat,
    };
  };

  const executeLicenseGeneration = async (
    pending: PendingLicenseGeneration,
    options?: { createContrat?: boolean; modeReglt?: string }
  ) => {
    const shouldCreateContrat = Boolean(options?.createContrat);
    let resolvedPending = pending;
    let clientCreated = false;
    let contratCreatedNow = false;

    try {
      setSubmittingGeneration(true);

      if (!resolvedPending.clientId) {
        const clientRes = await clientsApi.create(resolvedPending.newClient);
        const createdClientId = clientRes.data.id || clientRes.data.data?.id;

        if (!createdClientId) {
          showToast('Erreur: ID client non retourne', 'error');
          return;
        }

        clientCreated = true;
        // Client cree, parti de lui-meme vers Remote : il n'y est peut-etre pas arrive
        if (clientRes.data?.remote_warning) {
          showToast(clientRes.data.remote_warning, 'warning');
        }
        resolvedPending = {
          ...resolvedPending,
          clientId: createdClientId,
        };

        setMissingContratPrompt((prev) =>
          prev
            ? {
                ...prev,
                pending: {
                  ...prev.pending,
                  clientId: createdClientId,
                },
              }
            : prev
        );
      }

      if (shouldCreateContrat && !resolvedPending.contractCreated) {
        const today = new Date();
        const endDate = new Date(today);
        endDate.setFullYear(endDate.getFullYear() + 1);

        const contratResponse = await contratsApi.create({
          id_client: resolvedPending.clientId,
          date_debut: formatDateForApi(today),
          date_fin: formatDateForApi(endDate),
          t_contrat: 'Actif',
          mode_reglt: options?.modeReglt || DEFAULT_NEW_CONTRAT_PAYMENT_MODE,
        });

        contratCreatedNow = true;
        // Client deja envoye vers Remote dont le contrat n'a pas pu y etre mis a jour
        if (contratResponse.data?.remote_warning) {
          showToast(contratResponse.data.remote_warning, 'warning');
        }
        resolvedPending = {
          ...resolvedPending,
          hasContrat: true,
          contractCreated: true,
        };

        setMissingContratPrompt((prev) =>
          prev
            ? {
                ...prev,
                pending: {
                  ...prev.pending,
                  clientId: resolvedPending.clientId,
                  hasContrat: true,
                  contractCreated: true,
                },
              }
            : prev
        );
      }

      const response = await licensesApi.generate({
        ...resolvedPending.formData,
        client_id: resolvedPending.clientId,
      });

      setGeneratedKey(response.data.activation_key);

      if (response.data.existing) {
        setIsExistingKey(true);

        let infoMessage = 'Une licence active existe deja pour ce Hardware ID et ce type';
        if (clientCreated && contratCreatedNow) {
          infoMessage = 'Client et contrat crees. ' + infoMessage;
        } else if (clientCreated) {
          infoMessage = 'Client cree. ' + infoMessage;
        } else if (contratCreatedNow) {
          infoMessage = 'Contrat cree. ' + infoMessage;
        }

        showToast(infoMessage, 'info');
      } else {
        setIsExistingKey(false);

        if (clientCreated && contratCreatedNow) {
          showToast('Client, contrat et licence crees avec succes !', 'success');
        } else if (clientCreated) {
          showToast('Client cree et licence generee avec succes !', 'success');
        } else if (contratCreatedNow) {
          showToast('Contrat et licence generes avec succes !', 'success');
        } else {
          showToast('Licence generee avec succes !', 'success');
        }
      }

      setMissingContratPrompt(null);
      await loadData();
    } catch (err: any) {
      showToast(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la generation', 'error');
    } finally {
      setSubmittingGeneration(false);
    }
  };

  const _handleSubmitLegacy = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation hardware_id: exactement 32 caracteres
    if (formData.hardware_id.length !== 32) {
      showToast('Le Hardware ID doit faire exactement 32 caractères', 'error');
      return;
    }

    let clientId = formData.client_id;

    // Si nouveau client, le creer d'abord
    if (isNewClient) {
      if (!newClient.client_name.trim()) {
        showToast('Le nom du client est requis', 'error');
        return;
      }
      try {
        const clientRes = await clientsApi.create(newClient);
        clientId = clientRes.data.id || clientRes.data.data?.id;
        if (!clientId) {
          showToast('Erreur: ID client non retourné', 'error');
          return;
        }
        if (clientRes.data?.remote_warning) {
          showToast(clientRes.data.remote_warning, 'warning');
        }
      } catch (err: any) {
        showToast(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la création du client', 'error');
        return;
      }
    }

    if (!clientId || clientId === 0) {
      showToast('Veuillez sélectionner ou créer un client', 'error');
      return;
    }

    // Vérifier si le client a déjà un contrat, sinon en créer un automatiquement
    try {
      const contratsRes = await contratsApi.getAll();
      const contratsData = Array.isArray(contratsRes.data)
        ? contratsRes.data
        : contratsRes.data?.data || contratsRes.data?.contrats || [];
      const clientContrats = contratsData.filter((c: any) => c.id_client === clientId);

      if (clientContrats.length === 0) {
        const today = new Date();
        const dateDebut = today.toISOString().split('T')[0];
        const dateFin = new Date(today);
        dateFin.setFullYear(dateFin.getFullYear() + 1);
        const dateFinStr = dateFin.toISOString().split('T')[0];

        const contratResponse = await contratsApi.create({
          id_client: clientId,
          date_debut: dateDebut,
          date_fin: dateFinStr,
          t_contrat: 'Actif',
          mode_reglt: 'Nouvelle inst.',
        });
        showToast('Contrat créé automatiquement pour ce client (1 an, Nouvelle inst.)', 'info');
        if (contratResponse.data?.remote_warning) {
          showToast(contratResponse.data.remote_warning, 'warning');
        }
      }
    } catch (err: any) {
      showToast('Avertissement : impossible de vérifier/créer le contrat client', 'error');
    }

    try {
      const response = await licensesApi.generate({ ...formData, client_id: clientId });
      setGeneratedKey(response.data.activation_key);
      if (response.data.existing) {
        setIsExistingKey(true);
        showToast('Une licence active existe déjà pour ce Hardware ID et ce type', 'info');
      } else {
        setIsExistingKey(false);
        showToast(isNewClient ? 'Client créé et licence générée avec succès !' : 'Licence générée avec succès !', 'success');
      }
      loadData();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erreur lors de la génération', 'error');
    }
  };

  void _handleSubmitLegacy;

  const handleSubmitWithContratDecision = async (e: React.FormEvent) => {
    e.preventDefault();

    const pending = await buildPendingGeneration();
    if (!pending) {
      return;
    }

    if (!pending.hasContrat) {
      setMissingContratPrompt({
        pending,
        modeReglt: DEFAULT_NEW_CONTRAT_PAYMENT_MODE,
      });
      return;
    }

    await executeLicenseGeneration(pending, { createContrat: false });
  };

  const handleDeactivate = (id: number) => {
    showConfirm('Êtes-vous sûr de vouloir désactiver cette licence ?', async () => {
      try {
        await licensesApi.deactivate(id);
        showToast('Licence désactivée', 'success');
        loadData();
      } catch (err) {
        showToast('Erreur lors de la désactivation', 'error');
      }
    }, 'Désactiver la licence');
  };

  const handleReactivate = (id: number) => {
    showConfirm('Êtes-vous sûr de vouloir réactiver cette licence ?', async () => {
      try {
        await licensesApi.reactivate(id);
        showToast('Licence réactivée', 'success');
        loadData();
      } catch (err: any) {
        console.error('Reactivate error:', err?.response?.status, err?.response?.data, err?.message);
        const msg = err?.response?.data?.error || err?.response?.data?.message || `Erreur ${err?.response?.status || ''}: ${err?.message || 'Erreur lors de la réactivation'}`;
        showToast(msg, 'error');
      }
    }, 'Réactiver la licence');
  };

  const handleDelete = (license: License) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer définitivement la licence de "${license.client_name}" (${license.hardware_id}) ? Cette action est irréversible.`,
      async () => {
        try {
          await licensesApi.delete(license.id);
          showToast('Licence supprimée avec succès', 'success');
          loadData();
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erreur lors de la suppression';
          showToast(msg, 'error');
        }
      },
      'Supprimer la licence'
    );
  };

  const openEditLicenseModal = (license: License) => {
    setEditingLicense(license);
    setEditingClientId(license.client_id);
  };

  const closeEditLicenseModal = () => {
    if (savingLicenseClient) {
      return;
    }

    setEditingLicense(null);
    setEditingClientId(0);
  };

  const handleSaveLicenseClient = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingLicense) {
      return;
    }

    if (!editingClientId) {
      showToast('Veuillez selectionner un client', 'error');
      return;
    }

    if (editingClientId === editingLicense.client_id) {
      showToast('Aucune modification a enregistrer', 'info');
      closeEditLicenseModal();
      return;
    }

    try {
      setSavingLicenseClient(true);
      await licensesApi.update(editingLicense.id, { client_id: editingClientId });
      showToast('Client de la licence mis a jour avec succes', 'success');
      await loadData();
      setEditingLicense(null);
      setEditingClientId(0);
    } catch (err: any) {
      showToast(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Erreur lors de la mise a jour de la licence',
        'error'
      );
    } finally {
      setSavingLicenseClient(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await licensesApi.verify(verifyData.activation_key, verifyData.hardware_id);
      setVerifyResult(response.data);
    } catch (err: any) {
      setVerifyResult(err.response?.data || { valid: false, message: 'Erreur de vérification' });
    }
  };

  const handleEncode = async () => {
    try {
      const response = await licensesApi.encodePassword(textToEncode);
      setEncodedText(response.data.encoded);
    } catch (err) {
      showToast('Erreur lors de l\'encodage', 'error');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copié dans le presse-papiers !', 'success');
    } catch {
      // Fallback pour les navigateurs sans support clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast('Copié dans le presse-papiers !', 'success');
    }
  };

  const handleClientChange = (clientId: number) => {
    setFormData({ ...formData, client_id: clientId });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestion des Licences</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowVerifyModal(true)}>
            Vérifier une licence
          </button>
          <button className="btn btn-secondary" onClick={() => setShowEncoderModal(true)}>
            Encoder mot de passe
          </button>
          {hasPermission('licenses', 'create') && (
            <button className="btn btn-primary" onClick={handleGenerate}>
              + Générer Licence
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher par client ou Hardware ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Tous les types</option>
          {Array.from(new Set(licenses.map(l => l.license_type))).map((type) => (
            <option key={type} value={type}>{type.replace(/winalco\s*/i, '')}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Clé d'activation</th>
                <th>Type</th>
                <th>Hardware ID</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLicenses.map((license) => (
                <tr key={license.id} className={!license.is_active ? 'row-inactive' : ''}>
                  <td>{license.client_name}</td>
                  <td>
                    <code className="license-key">{license.activation_key}</code>
                    <button
                      className="btn-icon-small"
                      onClick={() => copyToClipboard(license.activation_key)}
                      title="Copier"
                    >
                      📋
                    </button>
                  </td>
                  <td>{license.license_type.replace(/winalco\s*/i, '')}</td>
                  <td>
                    <code className="license-key">{license.hardware_id}</code>
                    <button
                      className="btn-icon-small"
                      onClick={() => copyToClipboard(license.hardware_id)}
                      title="Copier"
                    >
                      📋
                    </button>
                  </td>
                  <td>
                    <span className={`status-badge ${license.is_active ? 'active' : 'inactive'}`}>
                      {license.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => setAuditLicense(license)}
                        title="Historique"
                      >
                        🕐
                      </button>
                      {canManageLicenses && (
                        <>
                          <button
                            className="btn-icon"
                            onClick={() => openEditLicenseModal(license)}
                            title="Editer le client"
                          >
                            <EditIcon />
                          </button>
                          {license.is_active ? (
                            <button
                              className="btn-icon"
                              onClick={() => handleDeactivate(license.id)}
                              title="Désactiver"
                            >
                              ⛔
                            </button>
                          ) : (
                            <button
                              className="btn-icon"
                              onClick={() => handleReactivate(license.id)}
                              title="Réactiver"
                            >
                              ✅
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={() => handleDelete(license)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredLicenses.length > ITEMS_PER_PAGE && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            «
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »»
          </button>

          <span className="pagination-info">
            {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredLicenses.length)} sur {filteredLicenses.length}
          </span>
        </div>
      )}

      {editingLicense && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Modifier le client de la licence</h2>
            <form onSubmit={handleSaveLicenseClient}>
              <div className="form-group">
                <label>Client actuel</label>
                <input type="text" value={editingLicense.client_name} disabled />
              </div>

              <div className="form-group">
                <label>Nouveau client</label>
                <SearchableSelect
                  options={clients.map((client) => ({
                    value: client.id,
                    label: client.client_name,
                  }))}
                  value={editingClientId}
                  onChange={(value) => setEditingClientId(value)}
                  placeholder="Saisir le nom du client..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de licence</label>
                  <input
                    type="text"
                    value={editingLicense.license_type.replace(/winalco\s*/i, '')}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label>Hardware ID</label>
                  <input type="text" value={editingLicense.hardware_id} disabled />
                </div>
              </div>

              <p className="info-message">
                Cette action corrige uniquement le client rattache a la licence. La cle d activation, le type et le Hardware ID restent inchanges.
              </p>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeEditLicenseModal}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingLicenseClient}>
                  {savingLicenseClient ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {auditLicense && (
        <AuditHistoryModal
          tableName="license_keys"
          recordId={auditLicense.id}
          recordLabel={auditLicense.client_name + ' - ' + auditLicense.hardware_id}
          fieldLabels={{
            id_client: 'Client',
            hardware_id: 'Hardware ID',
            activation_key: 'Clé d\'activation',
            license_type: 'Type',
            expiration_date: 'Date expiration',
            is_active: 'Actif',
            notes: 'Notes',
            revoked_by: 'Désactivé par',
          }}
          onClose={() => setAuditLicense(null)}
        />
      )}

      {/* Modal Génération */}
      {showModal && (
        <div className="modal-overlay">
          <div className={`modal ${generatedKey ? 'modal-wide' : ''}`}>
            <h2>Générer une nouvelle licence</h2>
            {generatedKey ? (
              <div className="generated-key-container">
                <p>{isExistingKey ? 'Clé d\'activation existante :' : 'Clé d\'activation générée :'}</p>
                <div className="generated-key">
                  <code>{generatedKey}</code>
                  <button
                    className="btn btn-secondary"
                    onClick={() => copyToClipboard(generatedKey)}
                  >
                    Copier
                  </button>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={closeGenerateModal}
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmitWithContratDecision}>
                <div className="form-group">
                  <div className="client-selection-header">
                    <label>Client</label>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => {
                        setIsNewClient(!isNewClient);
                        if (!isNewClient) {
                          setFormData({ ...formData, client_id: 0 });
                        } else {
                          setNewClient({ client_name: '', telephone: '', email: '', wilaya: '', ville: '' });
                        }
                      }}
                    >
                      {isNewClient ? '\u2190 S\u00e9lectionner un client existant' : '+ Nouveau client'}
                    </button>
                  </div>

                  {!isNewClient ? (
                    <SearchableSelect
                      options={clients.map((c) => ({
                        value: c.id,
                        label: c.client_name,
                      }))}
                      value={formData.client_id}
                      onChange={(val) => handleClientChange(val)}
                      placeholder="Saisir le nom du client..."
                      required
                    />
                  ) : (
                    <div className="new-client-fields">
                      <input
                        type="text"
                        value={newClient.client_name}
                        onChange={(e) => setNewClient({ ...newClient, client_name: e.target.value })}
                        placeholder="Nom du client *"
                        required
                      />
                      <div className="form-row">
                        <input
                          type="tel"
                          value={newClient.telephone}
                          onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })}
                          placeholder="Téléphone"
                        />
                        <input
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                          placeholder="Email (optionnel)"
                        />
                      </div>
                      <div className="form-row">
                        <select
                          value={newClient.wilaya}
                          onChange={(e) => setNewClient({ ...newClient, wilaya: e.target.value })}
                        >
                          <option value="">Wilaya</option>
                          {WILAYAS.map((w) => (
                            <option key={w} value={w}>{w}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={newClient.ville}
                          onChange={(e) => setNewClient({ ...newClient, ville: e.target.value })}
                          placeholder="Ville"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Hardware ID (32 caractères)</label>
                  <input
                    type="text"
                    value={formData.hardware_id}
                    onChange={(e) => setFormData({ ...formData, hardware_id: e.target.value })}
                    placeholder="Identifiant matériel unique (32 caractères)"
                    maxLength={32}
                    required
                  />
                  <small style={{ color: formData.hardware_id.length === 32 ? '#4caf50' : '#999' }}>
                    {formData.hardware_id.length}/32 caractères
                  </small>
                </div>

                <div className="form-group">
                  <label>Type de licence</label>
                  <select
                    value={formData.license_type}
                    onChange={(e) => setFormData({ ...formData, license_type: e.target.value })}
                    required
                  >
                    {LICENSE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeGenerateModal} disabled={submittingGeneration}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submittingGeneration}>
                    Générer
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal Vérification */}
      {missingContratPrompt && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Client sans contrat</h2>
            <p className="contract-decision-message">
              {`${missingContratPrompt.pending.clientName} n'a actuellement aucun contrat. Vous pouvez creer un contrat maintenant avec un mode de paiement, ou continuer la generation sans creer de contrat.`}
            </p>

            <div className="form-group">
              <label>Mode de paiement du nouveau contrat</label>
              <select
                value={missingContratPrompt.modeReglt}
                onChange={(e) =>
                  setMissingContratPrompt((prev) =>
                    prev
                      ? {
                          ...prev,
                          modeReglt: e.target.value,
                        }
                      : prev
                  )
                }
                disabled={submittingGeneration}
              >
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions contract-decision-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setMissingContratPrompt(null)}
                disabled={submittingGeneration}
              >
                Retour
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void executeLicenseGeneration(missingContratPrompt.pending, { createContrat: false })}
                disabled={submittingGeneration}
              >
                Continuer sans contrat
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  void executeLicenseGeneration(missingContratPrompt.pending, {
                    createContrat: true,
                    modeReglt: missingContratPrompt.modeReglt,
                  })
                }
                disabled={submittingGeneration}
              >
                {submittingGeneration ? 'Traitement...' : 'Creer le contrat et generer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerifyModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Vérifier une licence</h2>
            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label>Clé d'activation</label>
                <input
                  type="text"
                  value={verifyData.activation_key}
                  onChange={(e) => setVerifyData({ ...verifyData, activation_key: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Hardware ID</label>
                <input
                  type="text"
                  value={verifyData.hardware_id}
                  onChange={(e) => setVerifyData({ ...verifyData, hardware_id: e.target.value })}
                  required
                />
              </div>

              {verifyResult && (
                <div className={`verify-result ${verifyResult.valid ? 'valid' : 'invalid'}`}>
                  <p><strong>{verifyResult.valid ? '✓ Licence valide' : '✗ Licence invalide'}</strong></p>
                  <p>{verifyResult.message}</p>
                  {verifyResult.client_name && <p>Client: {verifyResult.client_name}</p>}
                  {verifyResult.license_type && <p>Type: {verifyResult.license_type}</p>}
                  {verifyResult.expires_at && (
                    <p>Expire le: {new Date(verifyResult.expires_at).toLocaleDateString()}</p>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowVerifyModal(false);
                    setVerifyResult(null);
                    setVerifyData({ activation_key: '', hardware_id: '' });
                  }}
                >
                  Fermer
                </button>
                <button type="submit" className="btn btn-primary">
                  Vérifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Encodeur */}
      {showEncoderModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Encoder un mot de passe</h2>
            <div className="form-group">
              <label>Texte à encoder</label>
              <input
                type="text"
                value={textToEncode}
                onChange={(e) => setTextToEncode(e.target.value)}
              />
            </div>

            {encodedText && (
              <div className="form-group">
                <label>Résultat encodé</label>
                <div className="encoded-result">
                  <code>{encodedText}</code>
                  <button
                    className="btn btn-secondary"
                    onClick={() => copyToClipboard(encodedText)}
                  >
                    Copier
                  </button>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowEncoderModal(false);
                  setTextToEncode('');
                  setEncodedText('');
                }}
              >
                Fermer
              </button>
              <button className="btn btn-primary" onClick={handleEncode}>
                Encoder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licenses;
